import { AttributeType, ColumnDescriptionDc, DetailedTableInfoDc, GeometryType, ResourceTypeLink } from "@evergis/api";
import { webMercator } from "@evergis/sgis/es/Crs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { isLocationMainSelector } from "../../../../ducks/location/selectors";
import { api } from "../../../../evergis/api";
import { DEFAULT_ID_ATTRIBUTE_NAME, GEOMETRY_ATTRIBUTE } from "../../../../evergis/constants";
import { EditGeometryType } from "../../../../evergis/core/attributes/geometryTypes";
import { useLimitResourceCount } from "../../../../hooks/notifications/useLimitResourceCount";
import { useNotification } from "../../../../hooks/notifications/useNotification";
import { useAddLayersToCurrentProject } from "../../../../hooks/project/useAddLayersToCurrentProject";
import { useDialogInfo } from "../../../../hooks/useDialogs";
import { createQueryLayer, createTileLayer } from "../../../../resources/layers";
import { project } from "../../../../resources/projects";
import { useNewLayerContext } from "../newLayerContext";

// eslint-disable-next-line max-lines-per-function
export const useNewLayerCreate = () => {
  const { t } = useTranslation("dialogs");
  const { name, systemName, tags, path, description, geometryType, attributes, attributesConfiguration } =
    useNewLayerContext();
  const [createQueryLayerService, responseLayer] = createQueryLayer.use();
  const [createTileLayerService, tileLayer] = createTileLayer.use();
  const addLayerToCurrentProject = useAddLayersToCurrentProject();
  const layerAdded = (!responseLayer.loading && responseLayer.data) || (!tileLayer.loading && tileLayer.data);
  const isProjectLocation = useSelector(isLocationMainSelector);
  const { closeDialog } = useDialogInfo();
  const [tableError, setTableError] = useState("");

  const parentId = useMemo(
    () => (path[path.length - 1] ? path[path.length - 1].resourceId : void 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path[path.length - 1]],
  );

  useNotification(layerAdded, {
    success: true,
    title: t("newLayer.layerCreated"),
    description: name,
  });

  useNotification(responseLayer.error || tableError || tileLayer.error, {
    error: true,
    title: t("newLayer.errorOccured"),
    description: responseLayer.error?.message || tableError || tileLayer.error?.message,
  });

  useLimitResourceCount(ResourceTypeLink.Layer, layerAdded);

  useLimitResourceCount(ResourceTypeLink.Table, layerAdded);
  // eslint-disable-next-line max-lines-per-function
  const onNewLayerCreate = useCallback(async () => {
    setTableError("");

    const defaultIdColumn = {
      type: AttributeType.Int64,
      name: DEFAULT_ID_ATTRIBUTE_NAME,
      autoincrement: true,
      hasIndex: true,
      isNullable: false,
      isUnique: false,
      isDisplayed: false,
    };

    let systemColumns: ColumnDescriptionDc[] = [defaultIdColumn];
    let additionalConfiguration: Partial<DetailedTableInfoDc> = {};

    if ([GeometryType.Polygon, GeometryType.Point, GeometryType.Polyline].includes(geometryType as GeometryType)) {
      systemColumns = [
        {
          name: GEOMETRY_ATTRIBUTE,
          srid: webMercator.wkid,
          type: (geometryType.charAt(0).toUpperCase() + geometryType.slice(1)) as unknown as AttributeType,
        },
        ...systemColumns,
      ];
    } else if (geometryType === EditGeometryType.Raster) {
      systemColumns = [
        {
          name: GEOMETRY_ATTRIBUTE,
          srid: webMercator.wkid,
          type: AttributeType.MultiPolygon,
        },
        ...systemColumns,
      ];
      additionalConfiguration = {
        configuration: {
          type: "tileCatalogTableConfiguration",
        },
      };
    }

    try {
      const { name: tableName } = await api.tables.create({
        name: systemName,
        alias: name,
        tags,
        description,
        geometries: [geometryType === EditGeometryType.Raster ? GeometryType.Polygon : (geometryType as GeometryType)],
        parentId,
        columns: [
          ...systemColumns,
          ...attributes
            .filter(({ columnName }) => columnName)
            .map(({ columnName, type, alias }) => ({ name: columnName, type, isNullable: true, alias })),
        ],
        ...additionalConfiguration,
      });

      if (geometryType === EditGeometryType.Raster) {
        await createTileLayerService({
          input: {
            name: systemName,
            alias: name,
            tags,
            description,
            parentId,
            isCogLayer: true,
            attributesConfiguration: {
              ...attributesConfiguration,
              attributes: [
                ...systemColumns.map(({ name: colName, type }) => ({
                  attributeName: colName,
                  type,
                  alias: "",
                  columnName: colName,
                })),
                ...attributes,
              ],
              tableName,
              idAttribute: DEFAULT_ID_ATTRIBUTE_NAME,
              geometryAttribute: GEOMETRY_ATTRIBUTE,
            },
          },
        });
      } else {
        await createQueryLayerService({
          input: {
            name: systemName,
            alias: name,
            tags,
            description,
            parentId,
            geometryType: (geometryType.charAt(0).toUpperCase() + geometryType.slice(1)) as unknown as AttributeType,
            eql: `SELECT * FROM ${tableName}`,
            srid: geometryType !== GeometryType.Unknown ? webMercator.wkid : null,
            attributesConfiguration: {
              ...attributesConfiguration,
              attributes: [
                ...systemColumns.map(({ name: colName, type }) => ({
                  attributeName: colName,
                  type,
                  alias: "",
                  columnName: colName,
                  isDisplayed: true,
                  isEditable: true,
                })),
                ...attributes,
              ],
              tableName,
              idAttribute: DEFAULT_ID_ATTRIBUTE_NAME,
              geometryAttribute: geometryType !== GeometryType.Unknown ? GEOMETRY_ATTRIBUTE : null,
            },
          },
        });
      }
    } catch (e) {
      setTableError(e.toString());
    }
  }, [
    name,
    systemName,
    tags,
    description,
    geometryType,
    attributes,
    parentId,
    attributesConfiguration,
    createQueryLayerService,
    createTileLayerService,
  ]);

  useEffect(() => {
    if (layerAdded) {
      if (isProjectLocation) {
        addLayerToCurrentProject([responseLayer.data || tileLayer.data]);
      }

      project.invalidateAll();
      closeDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProjectLocation, responseLayer, layerAdded, tileLayer]);

  return onNewLayerCreate;
};
